import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';

import {
  SocketHandler,
  Utils
} from '../../services';
import { helpers } from '../../helpers';
import { PageEventsHandler, Translate } from '../../services';

import { ViewActions } from '../view';
import { UserActions } from './user.actions';
import { AssessmentActions } from "../assessment";
import { UserSelectors } from './user.selectors';
import {
  REQUEST_FULLSCREEN,
  TRAINING_MODE
} from '../../constants';

@Injectable()
export class UserService {
  public user$: Observable<any>;
  public assessmentStarted$: Observable<boolean>;

  private translations: Object = {};
  private fullscreenExit$: Observable<any>;
  private translations$: Observable<any> = this.translate.getTranslations$();

  requestFullscreen: boolean;

  constructor(
    private socketHandler: SocketHandler,
    private utils: Utils,
    private pageEventsHandler: PageEventsHandler,
    private translate: Translate,
    private store: Store<any>,
    private userActions: UserActions,
    private viewActions: ViewActions,
    private userSelectors: UserSelectors,
    private assessmentActions: AssessmentActions,
  ) {
    this.requestFullscreen = REQUEST_FULLSCREEN && this.utils.isWeb();

    this.user$ = this.userSelectors.getUser();
    this.assessmentStarted$ = this.store.select(state => state.assessment.progress.started);

    this.userSelectors.getUserError()
      .subscribe(error => this._showAlert(error));

    this.user$
      .map(user => user.locked)
      .switchMap(isLocked => isLocked
        ? Observable.never()
        : this.socketHandler.connect$
          .withLatestFrom(this.getUser$()))
      .subscribe(([, user]) => {
        if (user && typeof user.sessionId !== 'undefined') {
          this.socketHandler.emit({
            messageId: 'fakeid',
            type: 'identify_by.request',
            payload: {
              sessionId: user.sessionId,
              testId: user.testId,
              userId: user.userId,
              trainingMode: TRAINING_MODE
            }
          });
          this.socketHandler.message$
            .filter(message => message.type.indexOf('identify_by.response.success') !== -1)
            .withLatestFrom(this.assessmentStarted$)
            .take(1)
            .subscribe(([message, isStarted]) => {
              // sync solo se il test ha già iniziato
              if(!isStarted) return;

              Observable.timer(1500)
                .take(1)
                .subscribe(() => {
                  this.store.dispatch(this.assessmentActions.forceViewTimeSync());
                });
            });
        }
      });

    this.fullscreenExit$ = this.pageEventsHandler.fullscreen$
      .filter(isFullscreen => !isFullscreen)
      .filter(() => !TRAINING_MODE)
      .filter(() => this.requestFullscreen);

    this.translations$
      .subscribe(translations => this.translations = translations);
  }

  _showAlert(text) {
    let toPrint = text
      , payload
    ;

    if (typeof text === 'object' &&
      text.message) {
      toPrint = decodeURI(text.message);
    }

    payload = {
      body: toPrint,
      type: 'danger'
    };

    if (typeof text.preventAutoclose === 'boolean') {
      payload.preventAutoclose = text.preventAutoclose
    }

    this.store.dispatch(this.viewActions.addAlert(payload));
    console.error(toPrint);
  }

  getUser$() {
    return this.userSelectors.getUser();
  }

  isLoading$(): Observable<boolean> {
    return this.userSelectors.getUserLoading();
  }

  isLoadingEnded$(): Observable<boolean> {
    return this.userSelectors.getUserIsLoadingEnded();
  }

  isLoaded$(): Observable<boolean> {
    return this.userSelectors.getUserIsLoaded();
  }

  isLogged$(): Observable<boolean> {
    return this.userSelectors.getUserLoaded();
  }

  loginRequest$(autoLogin, userId, password?) {
    let uid = this.utils.generateUID()
      , requestType = autoLogin
        ? 'training_authenticate'
        : 'authenticate';

    let _login = (_username, _password, _messageId, _auto) => {
      let _payload = _auto ?
        { id: _username } : { username: _username, password: _password };

      this.socketHandler.emit({
        messageId: _messageId,
        type: requestType + '.request',
        payload: _payload
      });
    };

    let terminalLoading$ = Observable.merge(
      this.isLoaded$(),
      this.isLoadingEnded$());
    let timeout$ = this.utils.requestTimeout$(this.translations['login#timeout']);

    let response$ = this.socketHandler.message$
      .takeUntil(Observable.merge(
        timeout$,
        terminalLoading$,
        this.fullscreenExit$,
        this.socketHandler.disconnect$
      ))
      .filter(({messageId}) => messageId === uid)
      .filter(message =>
        message.type === requestType + '.response.success' ||
        message.type === requestType + '.response.fail');
    let $return = response$
      .map(message => {
        return Object.assign({}, message, {
          success: message.type === requestType + '.response.success'
        });
      })
      .map(message => helpers.getNewObjectInDeepCamelCase(message))
      .take(1);

    this.fullscreenExit$
      .takeUntil(Observable.merge(
        response$,
        terminalLoading$
      ))
      .subscribe(() => this.store.dispatch(this.userActions.loginFail(
        this.translations['login#full_screen_exit']
      )));

    Observable.merge(
        timeout$,
        this.socketHandler.disconnect$.mapTo(this.translations['global#disconneced']))
      .takeUntil(Observable.merge(
        response$,
        terminalLoading$
      ))
      .subscribe(err => this.store.dispatch(this.userActions.loginFail(err)));

    _login(userId, password, uid, autoLogin);

    return $return;
  }

	getLanguage$(): Observable<string> {
		return this.userSelectors.getUserLanguage();
	}

	setLanguage(lang: string) {
		this.translate.setTranslations(lang);
	}

	getCookieLanguage(): string {
		return helpers.getCookie('user_language');
	}

	setCookieLanguage(lang: string): void {
		const current_val = this.getCookieLanguage();
		if(current_val !== lang){
			helpers.setCookie('user_language', lang, 120);
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/logics/user/user.service.ts